/**
 * The reader of a route's `capability` (`src/features/manifest.ts`), ADR 9037 — the route-level
 * half of ADR 9022's capability gate.
 *
 * `CapabilityGate` puts the reason in a CONTROL's place; this puts it in a SCREEN's place, for a
 * route whose whole purpose is one (kind, act) — the activity register (`activity · read`). The
 * no-permission state is the page's one strip in its no-permission form (brief part 3 A2 state 6),
 * and its sentence is the platform's reason and the roles that grant the act, so the person knows
 * whom to ask.
 *
 * Unknown is not "no" (ADR 9022 decision 3): while `/me/capabilities` loads, fails or is not
 * published the screen renders and the platform judges its read — a 403 there is the screen's own
 * no-permission state. A (kind, act) the platform's answer does not list is denied `not_declared`
 * (the contract's rule, `src/auth/capabilities.ts`).
 */

import type { ReactNode } from 'react';

import { useCapability } from '../../auth/capabilities';
import { useRoleWords, useWhyNotSentence } from '../../auth/capability-gate';
import type { RouteCapability } from '../../features/manifest';
import { PageStatusRegion, usePageStatusEntry } from '../../planning';

export function CapabilityRouteGate({ capability, children }: {
  readonly capability: RouteCapability | undefined;
  readonly children: ReactNode;
}): React.JSX.Element {
  if (capability === undefined) return <>{children}</>;
  return <Judged capability={capability}>{children}</Judged>;
}

/** A second component so the hook runs only for a route that declares a capability. */
function Judged({ capability, children }: {
  readonly capability: RouteCapability;
  readonly children: ReactNode;
}): React.JSX.Element {
  const verdict = useCapability(capability.kind, capability.action);
  if (verdict.state !== 'denied') return <>{children}</>;
  return <Denied verdict={verdict} />;
}

function Denied({ verdict }: {
  readonly verdict: Extract<ReturnType<typeof useCapability>, { state: 'denied' }>;
}): React.JSX.Element {
  return (
    <div data-capability-denied={verdict.reason}>
      <PageStatusRegion>
        <DeniedEntry verdict={verdict} />
      </PageStatusRegion>
    </div>
  );
}

/**
 * The page's one strip in its no-permission form (brief part 1 §4; part 3 A2 state 6): white, an
 * ink border, the padlock — «Questa pagina non è disponibile per il tuo ruolo.» and who may see
 * it, the roles the PLATFORM says grant the act (`grantedToRoles`), never a list written here.
 * Any other reason is the platform's own sentence. No table and no silhouette: nothing is coming.
 */
function DeniedEntry({ verdict }: {
  readonly verdict: Extract<ReturnType<typeof useCapability>, { state: 'denied' }>;
}): null {
  const why = useWhyNotSentence(verdict);
  // The roles in words (DEC-111, issue 203 finding 3) — the platform's list, the realm's names.
  const roles = useRoleWords(verdict.grantedToRoles);
  const detail = verdict.reason === 'not_granted' && roles.length > 0
    ? { key: 'capability.route.seenBy', values: { roles: roles.join(' · ') } }
    : { key: why.key, values: why.values };
  usePageStatusEntry({ kind: 'no-permission', message: { key: 'capability.route.unavailable' }, detail });
  return null;
}
